import type { ArtworkBoundingBoxObject } from '@sosocio/frontend-utils';
import { calculateArtworkSize } from '@sosocio/frontend-utils/artwork';
import type { OfferingModel } from 'interfaces/database';
import type {
	ArtworkSizeMetricUnits,
	PageModel,
	PageObjectModel,
	ProductDataModel,
} from 'interfaces/project';

interface ArtworkSizesOptions {
	excludeEmptyPages?: boolean;
}

/**
 * Rounds artwork size values to 2 decimal places and converts -0 to 0.
 *
 * @param value The artwork size value to round.
 * @returns The rounded artwork size value.
 */
function roundArtworkSizeValue(value: number): number {
	const roundedValue = Math.round((value + Number.EPSILON) * 100) / 100;

	return (
		Object.is(
			roundedValue,
			-0,
		)
			? 0
			: roundedValue
	);
}

/**
 * Gets all artwork objects on a page that have valid dimensions.
 *
 * @param pageModel {@link PageModel} to get objects from.
 * @param objects All objects in the product data, used to find the objects on the page.
 * @returns Array of artwork objects with valid dimensions on the page.
 */
function getPageArtworkObjects(
	pageModel: PageModel,
	objects: ProductDataModel['objects'],
): ArtworkBoundingBoxObject[] {
	if (
		pageModel.bgcolor
		&& pageModel.bgcolor.toLowerCase() !== 'transparent'
		&& Number.isFinite(pageModel.width)
		&& Number.isFinite(pageModel.height)
	) {
		return [
			{
				height: pageModel.height,
				width: pageModel.width,
				x_axis: 0,
				y_axis: 0,
			},
		];
	}

	if (!pageModel.objectList?.length) {
		return [];
	}

	return pageModel.objectList
		.map((objectId) => objects[objectId])
		.filter((objectModel): objectModel is PageObjectModel & ArtworkBoundingBoxObject => (
			!!objectModel
			&& Number.isFinite(objectModel.x_axis)
			&& Number.isFinite(objectModel.y_axis)
			&& Number.isFinite(objectModel.width)
			&& Number.isFinite(objectModel.height)
		));
}

/**
 * Calculates artwork dimensions for every page in the selected metric unit.
 *
 * @param productData Snapshot {@link ProductDataModel} to read from.
 * @param offeringModel {@link OfferingModel} that provides the config DPI.
 * @param metricUnits {@link ArtworkSizeMetricUnits} to store with the dimensions.
 * @param options Additional options for artwork size calculation.
 * @returns Artwork sizes keyed by page id.
 */
export function getArtworkSizesForProductData(
	productData: ProductDataModel,
	offeringModel: OfferingModel,
	metricUnits: ArtworkSizeMetricUnits,
	options: ArtworkSizesOptions = {},
): ProductDataModel['artworkSizes'] {
	const artworkSizes: NonNullable<ProductDataModel['artworkSizes']> = {};
	const pageModels = Object.values(productData.pages);

	if (productData.spine) {
		pageModels.push(productData.spine);
	}

	for (const pageModel of pageModels) {
		const pageArtworkObjects = getPageArtworkObjects(
			pageModel,
			productData.objects,
		);

		if (
			options.excludeEmptyPages
			&& !pageArtworkObjects.length
		) {
			continue;
		}

		const artworkSize = calculateArtworkSize(
			pageArtworkObjects,
			offeringModel.configdpi,
		);
		const size = artworkSize[metricUnits];

		artworkSizes[pageModel.id] = {
			height: roundArtworkSizeValue(size.height),
			metricUnits,
			width: roundArtworkSizeValue(size.width),
			x: roundArtworkSizeValue(size.x),
			y: roundArtworkSizeValue(size.y),
		};
	}

	return artworkSizes;
}

/**
 * Adds artwork dimensions to snapshot product data.
 *
 * @param productData Snapshot {@link ProductDataModel} to mutate.
 * @param offeringModel {@link OfferingModel} that provides the config DPI.
 * @param metricUnits {@link ArtworkSizeMetricUnits} to store with the dimensions.
 * @param options Additional options for artwork size calculation.
 */
export function addArtworkSizesToProductData(
	productData: ProductDataModel,
	offeringModel: OfferingModel,
	metricUnits: ArtworkSizeMetricUnits,
	options: ArtworkSizesOptions = {},
): void {
	productData.artworkSizes = getArtworkSizesForProductData(
		productData,
		offeringModel,
		metricUnits,
		options,
	);
}
